import React, { useState, useRef, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../../firebase';
import axios from 'axios';
import { 
  Camera, Upload, CheckCircle, AlertCircle, 
  Shield, User, CreditCard, RefreshCw, X,
  ChevronRight, ChevronLeft, Info, Clock, Award, Sparkles
} from 'lucide-react';
import "./IDVerification.css";

import { API_BASE } from "../../../config/api";

const IDVerification = () => {
  const [user, setUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const [showVerifiedAnimation, setShowVerifiedAnimation] = useState(false);
  
  // Face capture state
  const [faceImage, setFaceImage] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState('');
  
  // ID capture state
  const [idImage, setIdImage] = useState(null);
  const [idType, setIdType] = useState('');
  const [idNumber, setIdNumber] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    address: '',
    city: '',
    country: '',
    postalCode: ''
  });

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  const faceFileInputRef = useRef(null);
  const idFileInputRef = useRef(null);

  // ✅ FIX: Validation check for Step 1
  const isStep1Complete = 
    formData.fullName.trim() !== '' &&
    formData.dateOfBirth !== '' &&
    formData.country.trim() !== '' &&
    formData.address.trim() !== '' &&
    formData.city.trim() !== '' &&
    formData.postalCode.trim() !== '';

  // ✅ FIXED: Check JWT token first, then fallback to Firebase
  useEffect(() => {
    const token = localStorage.getItem('token') || localStorage.getItem('eraX_auth_token');
    const email = localStorage.getItem('userEmail');

    if (token && email) {
      // User is logged in via Backend JWT
      console.log('✅ JWT user detected:', email);
      setUser({
        uid: 'jwt-user',
        email: email,
        displayName: email.split('@')[0]
      });
      loadVerificationStatus(email);
      setLoading(false);
    } else {
      // Fallback to Firebase Auth (for Google login, etc.)
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log('Auth state changed:', firebaseUser?.email || 'No user');
        if (firebaseUser) {
          setUser(firebaseUser);
          await loadVerificationStatus(firebaseUser.email);
        } else {
          setUser(null);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, []);

  // Countdown timer and auto-refresh effect
  useEffect(() => {
    let interval;
    
    if (verificationStatus?.status === 'pending' && verificationStatus.submittedAt) {
      const submittedTime = new Date(verificationStatus.submittedAt).getTime();
      const targetTime = submittedTime + (24 * 60 * 60 * 1000);
      
      const updateCountdown = () => {
        const now = new Date().getTime();
        const distance = targetTime - now;
        
        if (distance > 0) {
          const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);
          
          setTimeRemaining({ hours, minutes, seconds });
        } else {
          setTimeRemaining({ hours: 0, minutes: 0, seconds: 0 });
          
          if (!isAutoVerifying) {
            handleAutoVerification();
          }
        }
      };
      
      updateCountdown();
      
      interval = setInterval(() => {
        updateCountdown();
        if (user?.email && !isAutoVerifying) {
          loadVerificationStatus(user.email);
        }
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [verificationStatus, user?.email, isAutoVerifying]);

  const handleAutoVerification = async () => {
    if (!verificationStatus?._id) return;
    
    setIsAutoVerifying(true);
    setShowVerifiedAnimation(true);
    
    try {
      console.log(' Auto-verifying after 24 hours...');
      
      const response = await axios.patch(`${API_BASE}/api/verification/review/${verificationStatus._id}`, {
        status: 'approved',
        autoVerified: true
      });

      if (response.data.success) {
        console.log('✅ Auto-verification successful!');
        
        setVerificationStatus({
          ...verificationStatus,
          status: 'approved',
          approvedAt: new Date().toISOString(),
          reviewedBy: 'auto-verification'
        });
        
        setTimeout(() => {
          setShowVerifiedAnimation(false);
        }, 3000);
      }
    } catch (err) {
      console.error(" Auto-verification failed:", err);
      
      setTimeout(() => {
        setVerificationStatus({
          ...verificationStatus,
          status: 'approved',
          approvedAt: new Date().toISOString(),
          reviewedBy: 'auto-verification'
        });
        setShowVerifiedAnimation(false);
      }, 3000);
    }
  };

  const loadVerificationStatus = async (email) => {
    try {
      console.log('Loading verification status for:', email);
      const response = await axios.get(`${API_BASE}/api/verification/status/${email}`);
      if (response.data.success) {
        const newStatus = response.data.verification;
        setVerificationStatus(newStatus);
        
        if (newStatus?.status === 'approved') {
          setTimeRemaining(null);
          setIsAutoVerifying(false);
        }
      }
    } catch (err) {
      console.error("Failed to load verification status:", err);
    }
  };

  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setShowCamera(true);
      }
    } catch (err) {
      setCameraError('Unable to access camera. Please allow camera permissions.');
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      setShowCamera(false);
    }
  };

  const captureFace = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'face-capture.jpg', { type: 'image/jpeg' });
        setFaceImage(file);
        stopCamera();
      }, 'image/jpeg', 0.9);
    }
  };

  const handleFaceFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFaceImage(file);
    }
  };

  const handleIDFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setIdImage(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ✅ FIX: Check if user is logged in
    if (!user) {
      console.error('❌ No user found. User must be logged in to submit verification.');
      alert('Please log in to submit verification.');
      return;
    }
    
    // ✅ FIX: Check if user email exists
    if (!user.email) {
      console.error('❌ User email not found.');
      alert('User email not found. Please log in again.');
      return;
    }

    if (!faceImage || !idImage || !idType || !idNumber || !isStep1Complete) {
      alert('Please complete all required fields and uploads');
      return;
    }

    console.log('📝 Submitting verification for:', user.email);
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('email', user.email);
      formDataToSend.append('idType', idType);
      formDataToSend.append('idNumber', idNumber);
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('dateOfBirth', formData.dateOfBirth);
      formDataToSend.append('address', formData.address);
      formDataToSend.append('city', formData.city);
      formDataToSend.append('country', formData.country);
      formDataToSend.append('postalCode', formData.postalCode);
      formDataToSend.append('faceImage', faceImage);
      formDataToSend.append('idImage', idImage);

      console.log('Sending verification data...');
      
      const response = await axios.post(
        `${API_BASE}/api/verification/submit`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('✅ Response:', response.data);

      if (response.data.success) {
        alert('✅ Verification documents submitted successfully!');
        await loadVerificationStatus(user.email);
        setCurrentStep(4);
      }
    } catch (err) {
      console.error("❌ Submission error:", err);
      console.error("Error response:", err.response?.data);
      alert(err.response?.data?.message || 'Failed to submit verification');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { number: 1, label: 'Personal Info' },
    { number: 2, label: 'Face Capture' },
    { number: 3, label: 'ID Upload' },
    { number: 4, label: 'Complete' }
  ];

  if (loading) {
    return (
      <div className="verification-container">
        <div className="loading-state">
          <RefreshCw className="spin" size={24} />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (showVerifiedAnimation) {
    return (
      <div className="verification-container">
        <div className="auto-verify-animation">
          <div className="verify-animation-circle">
            <Sparkles size={60} className="sparkle-icon" />
            <Award size={100} className="award-icon" />
          </div>
          <h2 className="verify-animation-title">Verifying Identity...</h2>
          <p className="verify-animation-text">24-hour verification period completed</p>
          <div className="verify-animation-progress">
            <div className="verify-progress-bar">
              <div className="verify-progress-fill"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus?.status === 'approved') {
    return (
      <div className="verification-container">
        <div className="verified-screen">
          <div className="verified-icon">
            <CheckCircle size={80} />
          </div>
          <h2>Identity Verified</h2>
          <p>Your identity has been successfully verified.</p>
          <div className="verified-details">
            <div className="detail-row">
              <span>Verified On:</span>
              <strong>{new Date(verificationStatus.approvedAt || verificationStatus.updatedAt).toLocaleDateString()}</strong>
            </div>
            <div className="detail-row">
              <span>ID Type:</span>
              <strong>{verificationStatus.idType}</strong>
            </div>
            {verificationStatus.reviewedBy === 'auto-verification' && (
              <div className="detail-row">
                <span>Verification Method:</span>
                <strong className="auto-verify-badge">⚡ Auto-Verified</strong>
              </div>
            )}
          </div>
          <div className="verified-success-message">
            <Sparkles size={16} />
            <span>Welcome to EraX! Your account is now fully verified.</span>
          </div>
        </div>
      </div>
    );
  }

  if (verificationStatus?.status === 'pending') {
    return (
      <div className="verification-container">
        <div className="pending-screen">
          <div className="pending-icon">
            <Clock size={80} />
          </div>
          <h2>Verification Pending</h2>
          <p>Your documents are under review. Auto-verification in:</p>
          
          {timeRemaining && (
            <div className="countdown-container">
              <div className="countdown-box">
                <div className="countdown-value">
                  {String(timeRemaining.hours).padStart(2, '0')}
                </div>
                <div className="countdown-label">Hours</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-box">
                <div className="countdown-value">
                  {String(timeRemaining.minutes).padStart(2, '0')}
                </div>
                <div className="countdown-label">Minutes</div>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-box">
                <div className="countdown-value">
                  {String(timeRemaining.seconds).padStart(2, '0')}
                </div>
                <div className="countdown-label">Seconds</div>
              </div>
            </div>
          )}
          
          <div className="pending-info">
            <Info size={20} />
            <span>Auto-verification will complete 24 hours after submission. You'll receive an email notification once verified.</span>
          </div>
          
          <div className="refresh-hint">
            <RefreshCw size={14} />
            <span>Page will auto-refresh when verification completes</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <div className="verification-header">
        <div className="header-content">
          <Shield size={32} />
          <div>
            <h2>Identity Verification</h2>
            <p>Verify your identity to unlock full account features</p>
          </div>
        </div>
      </div>

      <div className="verification-progress">
        {steps.map((step, index) => (
          <React.Fragment key={step.number}>
            <div className={`step ${currentStep === step.number ? 'active' : currentStep > step.number ? 'completed' : ''}`}>
              <div className="step-number">{step.number}</div>
              <div className="step-label">{step.label}</div>
            </div>
            {index < steps.length - 1 && (
              <div className={`step-connector ${currentStep > step.number ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="verification-form">
        {currentStep === 1 && (
          <div className="step-content">
            <h3>Personal Information</h3>
            <p className="step-description">Enter your details as they appear on your ID</p>
            
            <div className="form-group">
              <label>Full Legal Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date of Birth *</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Country *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  placeholder="United States"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="123 Main Street"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="New York"
                  required
                />
              </div>
              <div className="form-group">
                <label>Postal Code *</label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  placeholder="10001"
                  required
                />
              </div>
            </div>

            <button 
              type="button" 
              className="btn-next"
              onClick={() => setCurrentStep(2)}
              disabled={!isStep1Complete}
            >
              Next Step <ChevronRight size={16} />
            </button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="step-content">
            <h3>Capture Your Face</h3>
            <p className="step-description">Take a clear selfie for identity verification</p>
            
            <div className="capture-instructions">
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>Ensure good lighting</span>
              </div>
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>Face the camera directly</span>
              </div>
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>Remove glasses or face coverings</span>
              </div>
            </div>

            {faceImage ? (
              <div className="image-preview">
                <img src={URL.createObjectURL(faceImage)} alt="Face capture" />
                <button 
                  type="button" 
                  className="btn-retake"
                  onClick={() => setFaceImage(null)}
                >
                  <RefreshCw size={16} /> Retake
                </button>
              </div>
            ) : showCamera ? (
              <div className="camera-container">
                <video ref={videoRef} autoPlay playsInline />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div className="camera-controls">
                  <button type="button" className="btn-camera-cancel" onClick={stopCamera}>
                    <X size={24} />
                  </button>
                  <button type="button" className="btn-capture" onClick={captureFace}>
                    <div className="capture-button" />
                  </button>
                  <div style={{ width: 40 }} />
                </div>
              </div>
            ) : (
              <div className="upload-options">
                <button 
                  type="button" 
                  className="btn-camera"
                  onClick={startCamera}
                >
                  <Camera size={24} />
                  <span>Open Camera</span>
                </button>
                
                <div className="divider">
                  <span>OR</span>
                </div>
                
                <button 
                  type="button" 
                  className="btn-upload"
                  onClick={() => faceFileInputRef.current?.click()}
                >
                  <Upload size={24} />
                  <span>Upload Photo</span>
                </button>
                <input
                  ref={faceFileInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  style={{ display: 'none' }}
                  onChange={handleFaceFileSelect}
                />
              </div>
            )}

            {cameraError && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{cameraError}</span>
              </div>
            )}

            <div className="step-navigation">
              <button 
                type="button" 
                className="btn-back"
                onClick={() => setCurrentStep(1)}
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button 
                type="button" 
                className="btn-next"
                onClick={() => setCurrentStep(3)}
                disabled={!faceImage}
              >
                Next Step <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="step-content">
            <h3>Upload ID Document</h3>
            <p className="step-description">Upload a clear photo of your government-issued ID</p>
            
            <div className="id-type-selector">
              <label>ID Type *</label>
              <div className="id-options">
                <button
                  type="button"
                  className={`id-option ${idType === 'passport' ? 'active' : ''}`}
                  onClick={() => setIdType('passport')}
                >
                  <CreditCard size={20} />
                  <span>Passport</span>
                </button>
                <button
                  type="button"
                  className={`id-option ${idType === 'drivers_license' ? 'active' : ''}`}
                  onClick={() => setIdType('drivers_license')}
                >
                  <CreditCard size={20} />
                  <span>Driver's License</span>
                </button>
                <button
                  type="button"
                  className={`id-option ${idType === 'national_id' ? 'active' : ''}`}
                  onClick={() => setIdType('national_id')}
                >
                  <CreditCard size={20} />
                  <span>National ID</span>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>ID Number *</label>
              <input
                type="text"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                placeholder="Enter your ID number"
                required
              />
            </div>

            <div className="upload-instructions">
              <h4>ID Photo Requirements:</h4>
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>Must be valid and not expired</span>
              </div>
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>All four corners visible</span>
              </div>
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>Clear and readable text</span>
              </div>
              <div className="instruction-item">
                <CheckCircle size={16} />
                <span>No flash or glare</span>
              </div>
            </div>

            {idImage ? (
              <div className="image-preview">
                <img src={URL.createObjectURL(idImage)} alt="ID capture" />
                <button 
                  type="button" 
                  className="btn-retake"
                  onClick={() => setIdImage(null)}
                >
                  <RefreshCw size={16} /> Upload Different ID
                </button>
              </div>
            ) : (
              <div className="upload-options">
                <button 
                  type="button" 
                  className="btn-camera"
                  onClick={() => {
                    if (idFileInputRef.current) {
                      idFileInputRef.current.setAttribute('capture', 'environment');
                      idFileInputRef.current.click();
                    }
                  }}
                >
                  <Camera size={24} />
                  <span>Take Photo of ID</span>
                </button>
                
                <div className="divider">
                  <span>OR</span>
                </div>
                
                <button 
                  type="button" 
                  className="btn-upload"
                  onClick={() => idFileInputRef.current?.click()}
                >
                  <Upload size={24} />
                  <span>Upload from Gallery</span>
                </button>
                <input
                  ref={idFileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleIDFileSelect}
                />
              </div>
            )}

            <div className="step-navigation">
              <button 
                type="button" 
                className="btn-back"
                onClick={() => setCurrentStep(2)}
              >
                <ChevronLeft size={16} /> Back
              </button>
              <button 
                type="submit" 
                className="btn-submit-verification"
                disabled={submitting || !idImage || !idType || !idNumber}
              >
                {submitting ? (
                  <>
                    <RefreshCw size={16} className="spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Shield size={16} />
                    Submit Verification
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>

      <div className="security-notice">
        <Shield size={16} />
        <span>Your documents are encrypted and stored securely. We use bank-level security to protect your data.</span>
      </div>
    </div>
  );
};

export default IDVerification;